/**
 * Legacy `foundation` export — maps semantic tokens for backward compatibility.
 * Prefer importing `semantic` / `components` from `src/ui/theme` in new code.
 */

import { semantic } from './theme/semanticTokens';
import { structure, spacePx } from './theme/componentTokens';

const { bg, fg, border, brand: brandSem, status: statusSem, role: roleSem, roleSoft: roleSoftSem, roleBorder: roleBorderSem, elevation, fontFamily } = semantic;

/** @deprecated Use `semantic.brand` / `semantic.fg` — kept for existing consumers */
export const foundation = {
  fontFamily,
  color: {
    brand: {
      navy950: fg.default,
      navy900: fg.default,
      navy800: fg.secondary,
      navy700: brandSem.primary,
      navy600: brandSem.primary,
      primary: brandSem.primary,
      primaryHover: brandSem.primaryHover,
      primaryMuted: brandSem.primaryMuted,
      primaryMutedBorder: brandSem.primaryMutedBorder,
      primaryMutedStrong: brandSem.primaryMutedStrong,
      /** Brand blue as TEXT/links: = primary in Light, a readable blue in Graphite. */
      text: brandSem.text,
      onPrimary: brandSem.onPrimary,
      stop: brandSem.stop,
      stopStrong: brandSem.stopStrong,
      /** Deep brand navy (accent 900) — active nav glyph body. */
      navy: brandSem.navy,
      routeLight: brandSem.routeLight,
    },
    neutral: {
      bg: bg.canvas,
      bgSubtle: bg.surfaceMuted,
      surface: bg.surface,
      /** RAISED layer: every floating layer (pop-up, menu, toast, bulk pill). Light = surface (white). */
      surfaceElevated: bg.surfaceElevated,
      surfaceMuted: bg.surfaceMuted,
      inverse: bg.inverse,        // solid dark surface (slate 800) — e.g. the bulk-run pill
      border: border.default,
      borderStrong: border.strong,
      /** Typing-field edge (3:1 on white in Light). */
      borderField: border.field,
      text: fg.default,
      textSecondary: fg.secondary,
      textMuted: fg.muted,
      ink2: fg.ink2,
      iconMuted: fg.icon,         // muted ICON / chevron grey (text uses textMuted)
      textDisabled: fg.disabled,
      /** Quiet readable text (placeholder-grey): = textDisabled in Light, but readable in Graphite. */
      textFaint: fg.faint,
      textInverse: fg.inverse,    // light text on `inverse`
    },
    role: {
      owner: roleSem.owner,
      admin: roleSem.admin,
      supervisor: roleSem.supervisor,
      operations: roleSem.operations,
      billing: roleSem.billing,
      worker: roleSem.worker,
      platformRole: roleSem.platformRole,
    },
    roleSoft: { ...roleSoftSem },
    roleBorder: { ...roleBorderSem },
    semantic: {
      success: statusSem.success,
      successIcon: statusSem.successIcon,
      successSoft: statusSem.successSoft,
      successBorder: statusSem.successBorder,
      successFaint: statusSem.successFaint,
      successFaintBorder: statusSem.successFaintBorder,
      successRing: statusSem.successRing,
      warning: statusSem.warning,
      warningFill: statusSem.warningFill,
      finishSave: statusSem.finishSave,
      dangerFill: statusSem.dangerFill,
      infoFill: statusSem.infoFill,
      warningSoft: statusSem.warningSoft,
      warningBorder: statusSem.warningBorder,
      warningFaint: statusSem.warningFaint,
      warningFaintBorder: statusSem.warningFaintBorder,
      warningStrong: statusSem.warningStrong,
      warningText: statusSem.warningText,
      danger: statusSem.danger,
      dangerSoft: statusSem.dangerSoft,
      dangerStrong: statusSem.dangerStrong,
      dangerFaint: statusSem.dangerFaint,
      dangerBorder: statusSem.dangerBorder,
      dangerOutline: statusSem.dangerOutline,
      info: statusSem.info,
      infoSoft: statusSem.infoSoft,
      infoBorder: statusSem.infoBorder,
      violet: statusSem.violet,
      violetSoft: statusSem.violetSoft,
      violetBorder: statusSem.violetBorder,
      operations: statusSem.operations,
      operationsSoft: statusSem.operationsSoft,
      operationsBorder: statusSem.operationsBorder,
    },
  },
  space: structure.space,
  spacePx,
  radii: structure.radii,
  shadow: {
    none: elevation.none,
    xs: elevation.xs,
    sm: elevation.sm,
    md: elevation.md,
    widget: elevation.widget,
    widgetHover: elevation.widgetHover,
    lg: elevation.floating,
    focus: elevation.focusRing,
    fieldFocus: elevation.fieldFocusRing,
    fieldFocusDanger: elevation.fieldFocusRingDanger,
  },
  fontSize: structure.fontSize,
  fontWeight: structure.fontWeight,
  lineHeight: structure.lineHeight,
};
